import React, {
    ChangeEvent,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import debounce from 'lodash/debounce';
import SearchInput from 'flow/components/ui-components/SearchInput';
import {
    convertNumberToE164,
    isRegisteredSIP,
    NewCallBlockReason,
    numberIsNationalOrInternational,
    removeExtraCharacters,
    stripAndConvertNumberToE164,
} from 'flow/utils/call-utils';
import {
    getIsPhoneConnected,
    hasActiveCallsBlockingNewCall,
} from 'flow/selectors/call-selectors';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import { useOutgoingCallsEnabled } from 'flow/hooks/api/useSoftphoneCallSettings';
import { getCountryCode, isCallableNumberTW } from 'flow/utils/extension-utils';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import AvatarPic from 'flow/components/ui-components/AvatarPic';
import BaseListItem from 'flow/components/ui-components/BaseListItem';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import DEFAULT_AVATAR_SVG from 'images/broken_image_person.svg';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useCustomer from 'flow/hooks/useCustomer';
import useExtension from 'flow/hooks/useExtension';
import useKeyboardShortcuts from 'flow/hooks/useKeyboardShortcuts';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getKeyboardShortcuts } from 'flow/selectors/keyboard-shortcuts-selectors';
import { normalizeShortcutToDisplay } from 'flow/utils/shortcut-utils';

const DEFAULT_DEBOUNCE_WAIT_TIME_MS = 350;

interface Props {
    onChange: (eventValue: string, filtertext: string) => void;
    initialSearchText: string;
    children?: React.ReactNode;
    enableShortcut?: boolean;
    enableMakingCalls?: boolean;
    searchInputClassName?: string;
    /** How long to wait after the last keystroke before reporting the text.
     *  The default suits a list that renders every match; lower it where the
     *  list is virtualised and so costs the same however much it matches. */
    debounceMs?: number;
}

// The ref is the search field.
const SearchBar = forwardRef<HTMLInputElement, Props>((props, ref) => {
    const {
        onChange,
        initialSearchText,
        children,
        enableShortcut,
        enableMakingCalls = true,
        searchInputClassName,
        debounceMs = DEFAULT_DEBOUNCE_WAIT_TIME_MS,
    } = props;
    const { i18next, embedded } = useAppContext();
    const { ownExtension } = useExtension();
    const { ownContact } = useContact();
    const { ownCustomer } = useCustomer();
    const dispatch = useAppDispatch();
    const { callSearchShortcut } = useKeyboardShortcuts();
    const shortcuts = useAppSelector(getKeyboardShortcuts);

    const textFieldRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => textFieldRef.current, []);
    const [filterText, setFilterText] = useState(initialSearchText);

    const userCountryCode = getCountryCode(ownContact, ownCustomer);
    const hasConnectedPhone = useAppSelector(getIsPhoneConnected);
    const outgoingCallsEnabled = useOutgoingCallsEnabled();
    const isBlockedForNewCall = useAppSelector(
        (state) =>
            hasActiveCallsBlockingNewCall(state, 'outgoing') !==
            NewCallBlockReason.NONE,
    );

    const handleKeyDown = (event: KeyboardEvent) => {
        const isSearchShortcut = callSearchShortcut('general', event);

        if (isSearchShortcut && enableShortcut) {
            event.preventDefault();
            textFieldRef.current.focus();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    });

    const handleSearch = (eventValue: string) => {
        if (onChange) {
            onChange(eventValue, filterText);
        }
    };

    /* Read through a ref so the debounced function can be held across renders
     * - rebuilding it every render would restart the wait on each keystroke
     * instead of measuring it from the last one. */
    const handleSearchRef = useRef(handleSearch);
    handleSearchRef.current = handleSearch;

    const completeSearchDebounced = useMemo(
        () =>
            debounce(
                (eventValue: string) => handleSearchRef.current(eventValue),
                debounceMs,
            ),
        [debounceMs],
    );

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value);
        completeSearchDebounced(event.target.value);
    };

    /**
     * On 'ENTER': make a call
     */
    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            // RETURN
            const strippedNumber = removeExtraCharacters(filterText);
            if (
                numberIsNationalOrInternational(strippedNumber) &&
                !isBlockedForNewCall &&
                !isTeamsTypeIntegration(embedded) &&
                outgoingCallsEnabled
            ) {
                const callableNumber = convertNumberToE164(
                    strippedNumber,
                    userCountryCode,
                );
                dispatch(showCallDialog(callableNumber));
            }
        }
    };

    const validCallState =
        !isTeamsTypeIntegration(embedded) &&
        (hasConnectedPhone || isRegisteredSIP(ownExtension));

    const callableNumber = stripAndConvertNumberToE164(
        filterText,
        userCountryCode,
    );
    const shortcutText = (type: string) => {
        const matchedShortcut = shortcuts.find(
            (shortcut) =>
                shortcut.get('enabled') && shortcut.get('label') === type,
        );
        if (!matchedShortcut) {
            return '';
        }

        const shortcutText = normalizeShortcutToDisplay(
            matchedShortcut.get('value'),
        );

        return `[${shortcutText}]`;
    };
    const searchText = validCallState
        ? i18next.t('input.searchAndCall')
        : i18next.t('input.search');

    return (
        <>
            <div className="flex items-center justify-between gap-3">
                <SearchInput
                    iconKey="search"
                    placeholder={
                        enableShortcut
                            ? `${searchText} ${shortcutText('extension_list_search')}`
                            : searchText
                    }
                    onChange={handleInputChange}
                    onKeyUp={handleKeyUp}
                    value={filterText}
                    ref={textFieldRef}
                    className={searchInputClassName}
                />
                {children}
            </div>

            {enableMakingCalls && isCallableNumberTW(callableNumber) ? (
                <div className="py-3">
                    <BaseListItem>
                        <AvatarPic
                            className="group-hover:bg-gray-300"
                            src={DEFAULT_AVATAR_SVG}
                        />

                        <div className="flex flex-1 flex-col">
                            <div className="m-auto flex w-full flex-row">
                                <div className="overflow-hidden text-ellipsis">
                                    {callableNumber}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <CallAdaptiveButton
                                activeBg={true}
                                phoneNumber={callableNumber}
                            />
                        </div>
                    </BaseListItem>
                </div>
            ) : null}
        </>
    );
});
SearchBar.displayName = 'SearchBar';

export default SearchBar;
